import { FileTextOutlined } from '@ant-design/icons';

import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { TabsTruot } from '../../../components/shared';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import type { DocTranscript } from '../types';

/**
 * Bản chép OCR từng trang (`GET …/transcript`) của chứng từ hay của một bản trong kho. Nhiều tệp
 * thì chọn tệp bằng thẻ; mỗi trang một khối có số trang. Nội dung là dữ liệu nghiệp vụ, không dịch.
 */
export function XemBanChep({
  banChep,
  dangTai,
  loi,
  onThuLai,
}: {
  banChep: DocTranscript | undefined;
  dangTai: boolean;
  loi: unknown;
  onThuLai: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();

  if (dangTai) {
    return (
      <div className="space-y-3 p-5">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (loi) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-5">
        <p className="text-sm text-danger-700">{t('ocr.loiBanChep', { loi: dichLoi(loi) })}</p>
        <Button variant="outline" size="sm" onClick={onThuLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }
  const files = banChep?.files ?? [];
  if (files.length === 0) {
    return <Empty icon={FileTextOutlined} title={t('ocr.chuaCoBanChep')} description={t('ocr.chuaCoBanChepMo')} />;
  }
  const noiDung = (tep: DocTranscript['files'][number]) => (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
        <span>{t('ocr.soTrang', { so: soVN(tep.pages.length, locale) })}</span>
        {tep.reader_label && <span>· {t('ocr.docBang', { ten: tep.reader_label })}</span>}
        {tep.transcribed_at && <span>· {ngayGioVN(tep.transcribed_at, locale)}</span>}
        {tep.is_text_layer && <Badge variant="neutral">{t('ocr.lopVanBanPdf')}</Badge>}
        {tep.reused && <Badge variant="neutral">{t('ocr.dungLaiTuKho')}</Badge>}
      </div>
      {tep.pages.map((p) => (
        <section key={p.page_number} aria-label={t('ocr.trangSo', { so: p.page_number })} className="rounded-lg border border-default">
          <h3 className="border-b border-default bg-subtle px-4 py-2 text-xs font-medium text-content-secondary">
            {t('ocr.trangSo', { so: p.page_number })}
          </h3>
          <pre className="max-h-[480px] overflow-auto whitespace-pre-wrap break-words px-4 py-3 font-mono text-xs leading-relaxed text-content-primary">
            {p.content}
          </pre>
        </section>
      ))}
    </div>
  );

  return (
    <div className="p-4">
      {files.length === 1 ? (
        noiDung(files[0]!)
      ) : (
        <TabsTruot
          gon
          items={files.map((f, i) => ({
            key: String(i),
            label: f.file_name ?? t('ocr.tepSo', { so: i + 1 }),
            children: noiDung(f),
          }))}
        />
      )}
    </div>
  );
}
